import React, { forwardRef, useMemo } from 'react';
import { Autocomplete, Box, TextField, type TextFieldProps } from '@mui/material';

import { PLACE_OF_SERVICE_CODES } from '../../types';

type PlaceOfServiceSelectProps = Omit<TextFieldProps, 'select' | 'children' | 'onChange'> & {
  value?: string;
  /**
   * Event-shaped change handler kept for drop-in compatibility with the previous
   * `<TextField select>` implementation. Autocomplete hands back `(_, option)`, but
   * every call site (SessionDetailsSection, AppointmentTypeModal, CMS-1500 Box 24,
   * …) reads `event.target.value`, so we synthesize a matching event.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  emptyLabel?: string;
};

// Most commonly used POS codes, pinned to the top of the list in this order.
const COMMON_POS_CODES = ['02', '10', '11', '12', '21'];

type PlaceOfServiceOption = {
  code: string;
  name: string;
  isCommon: boolean;
};

const getOptionLabel = (option: PlaceOfServiceOption) =>
  option.name ? `${option.code} - ${option.name}` : option.code;

// Common codes first (in COMMON_POS_CODES order), then everything else by code.
const PLACE_OF_SERVICE_OPTIONS: PlaceOfServiceOption[] = (() => {
  const entries = Object.entries(PLACE_OF_SERVICE_CODES);

  const common = entries
    .filter(([code]) => COMMON_POS_CODES.includes(code))
    .sort((a, b) => COMMON_POS_CODES.indexOf(a[0]) - COMMON_POS_CODES.indexOf(b[0]))
    .map(([code, name]) => ({ code, name, isCommon: true }));

  const others = entries
    .filter(([code]) => !COMMON_POS_CODES.includes(code))
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([code, name]) => ({ code, name, isCommon: false }));

  return [...common, ...others];
})();

/**
 * Place of Service picker for CMS-1500 Box 24B.
 *
 * A filtered MUI `Autocomplete` (mirroring `ICD10CodePicker` / `CPTCodePicker`):
 * type-to-filter by code or description, with the common codes
 * (02/10/11/12/21) pinned to the top. Exposes the same event-shaped
 * `onChange(event.target.value)` API as the previous `<TextField select>` so it
 * stays a drop-in replacement for all existing call sites.
 */
export const PlaceOfServiceSelect = forwardRef<HTMLDivElement, PlaceOfServiceSelectProps>(
  (
    { value = '', size = 'small', sx, slotProps, emptyLabel, onChange, disabled, fullWidth, ...props },
    ref,
  ) => {
    const selectedOption = useMemo(
      () => PLACE_OF_SERVICE_OPTIONS.find(option => option.code === value) ?? null,
      [value],
    );

    const placeholder = emptyLabel ?? (size === 'small' ? '--' : undefined);

    // Some call sites (SessionDetailsFields compact mode) drive the dropdown's
    // open state externally via `slotProps.select.{open,onOpen,onClose}` — a
    // hold-over from the `<TextField select>` API. Map it onto the Autocomplete's
    // native open props so those call sites keep working with zero changes. The
    // caller's remaining slotProps (e.g. `inputLabel`) still flow to the inner
    // TextField untouched (the `select` slot is inert on a non-select TextField).
    const selectSlot =
      slotProps && typeof slotProps.select === 'object' ? slotProps.select : undefined;
    const openControlProps: Pick<
      React.ComponentProps<typeof Autocomplete>,
      'open' | 'onOpen' | 'onClose'
    > = typeof selectSlot?.open === 'boolean'
      ? { open: selectSlot.open, onOpen: selectSlot.onOpen, onClose: selectSlot.onClose }
      : {};

    const callerSlotProps = typeof slotProps === 'object' && slotProps ? slotProps : {};
    // When a placeholder is shown, keep the floating label shrunk so it doesn't
    // overlap the placeholder — this mirrors the old `<TextField select displayEmpty>`
    // behavior. Callers can still override via their own `slotProps.inputLabel`.
    const inputLabelSlotProps =
      placeholder != null
        ? { shrink: true, ...(callerSlotProps.inputLabel as object | undefined) }
        : callerSlotProps.inputLabel;

    const handleChange = (
      _event: React.SyntheticEvent,
      option: PlaceOfServiceOption | null,
    ) => {
      // Re-shape Autocomplete's `(_, option)` into the event the call sites expect.
      onChange?.({
        target: { value: option?.code ?? '' },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <Autocomplete<PlaceOfServiceOption>
        ref={ref}
        value={selectedOption}
        onChange={handleChange}
        options={PLACE_OF_SERVICE_OPTIONS}
        size={size}
        disabled={disabled}
        fullWidth={fullWidth}
        autoHighlight
        {...openControlProps}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, selected) => option.code === selected.code}
        groupBy={option => (option.isCommon ? 'Common' : 'All places of service')}
        filterOptions={(options, state) => {
          const query = state.inputValue.trim().toLowerCase();
          const selectedOptionLabel = selectedOption
            ? getOptionLabel(selectedOption).toLowerCase()
            : null;

          // MUI fills inputValue with the selected option's label. Treat that as
          // an unopened filter so a populated field still shows every choice
          // when the user reopens it to select a different place of service.
          if (!query || query === selectedOptionLabel) return options;
          // Preserve common-first ordering — just narrow to matches on code or name.
          return options.filter(
            option =>
              option.code.toLowerCase().includes(query) ||
              option.name.toLowerCase().includes(query),
          );
        }}
        renderOption={(optionProps, option) => {
          const { key, ...otherProps } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
            key?: React.Key;
          };
          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{ fontSize: '13px', fontWeight: option.isCommon ? 500 : 400 }}
            >
              <span style={{ fontFamily: 'monospace', marginRight: '8px' }}>{option.code}</span>
              {option.name}
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            {...props}
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              // Caller slotProps (e.g. `inputLabel: { shrink: true }`) are merged in;
              // any `select` slot is inert here and is consumed as open-control above.
              ...callerSlotProps,
              inputLabel: inputLabelSlotProps,
            }}
          />
        )}
        sx={{
          minWidth: '50px',
          // Compact defaults for dense contexts (e.g. the CMS-1500 Box 24 table,
          // which passes no sx of its own). Call sites that need more room override
          // these via their own `sx`.
          ...(size === 'small' && {
            '& .MuiInputBase-input': {
              padding: '2px 4px',
              fontSize: '12px',
              textAlign: 'left',
            },
            '& .MuiOutlinedInput-root': {
              height: '24px',
            },
          }),
          // eslint-disable-next-line @typescript-eslint/no-misused-spread
          ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
        }}
        slotProps={{
          // Elevate above modals/popovers (e.g. AppointmentTypeModal, the signing
          // popover) so the dropdown is never clipped behind the container.
          popper: { sx: { zIndex: theme => theme.zIndex.modal + 300 } },
          listbox: { sx: { maxHeight: 320 } },
        }}
      />
    );
  },
);

PlaceOfServiceSelect.displayName = 'PlaceOfServiceSelect';
